import { useMemo, useRef, useState, memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Department, DepartmentFilterInputObject } from 'graphql-common';
import ModalTableMultiselect, {
  useModalTableAllTabManager,
} from '@lib/components/ModalTableMultiselect/ModalTableMultiselect';
import { FormRefType } from '@lib/components/ReactHookForm/types';
import useModalTableModeManager from '@lib/components/ModalTableMultiselect/hooks/useModalTableModeManager';
import useSelectedFormFieldIds from '@lib/components/ModalTableMultiselect/hooks/useSelectedFormFieldIds';
import { ModalTableMultiselectMode } from '@lib/components/ModalTableMultiselect/enums';
import useDepartmentsQueryHook from 'utils/fetch/hooks/useDepartmentsQueryHook';
import useDepartmentsTableTree from 'routes/Departments/utils/useDepartmentsTableTree';
import getDepartmentsTableColumns from 'routes/Departments/utils/getDepartmentsTableColumns';

// Unlike Asset/Task/etc., `DepartmentFilterInputObject` has no generic
// `id`-IN filter — the Selected tab must fetch via an entity-specific reverse
// filter (`departmentTaskGroupsGroupKey`, `departmentInterventionsInterventionId`)
// passed in via `selectedTabFilters`, or fall back to a locally accumulated
// known-departments cache when none is available (create flow, mirrors
// FormTemplatesModalTableMultiselect). SelectedOnly mode can't honestly
// display the selection in create flow either, so — like FormTemplates —
// this wrapper doesn't expose a `mode` prop.

type DepartmentsModalTableMultiselectProps = {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (values: string[]) => void;
  formRef: FormRefType;
  fieldName?: string;
  selectedTabFilters?: DepartmentFilterInputObject[];
};

function DepartmentsModalTableMultiselect({
  isOpen,
  onClose,
  onSelect,
  formRef,
  fieldName = 'departmentLinks',
  selectedTabFilters,
}: DepartmentsModalTableMultiselectProps) {
  const { t } = useTranslation();
  const [isFetchingAllIds, setIsFetchingAllIds] = useState(false);

  const selectedDepartments = useSelectedFormFieldIds(formRef, fieldName);

  const filterStateDefault = useMemo(() => ({}), []);

  const {
    activeTab,
    activeTabState,
    clearFilter,
    filterState,
    filterStateSelected,
    idsForSelectedTabQuery,
    isSelectedTabActive,
    orderingState,
    orderingStateSelected,
    paginationState,
    paginationStateSelected,
    searchState,
    searchStateSelected,
    setSelectedIdsForQuery,
  } = useModalTableModeManager({
    isOpen,
    mode: ModalTableMultiselectMode.WithTabs,
    selectedIds: selectedDepartments,
    filterStateDefault,
  });

  const { shouldRunAllQuery, handleAllTabQueryResult, getAllTabSnapshot } =
    useModalTableAllTabManager<Department>({
      isOpen,
      mode: ModalTableMultiselectMode.WithTabs,
      activeTab,
      isSelectedOnlyMode: false,
    });

  // All tab query
  const {
    collection,
    loading: departmentsLoading,
    firstLoading: departmentsFirstLoading,
    totalCount,
    fetchIds,
  } = useDepartmentsQueryHook({
    searchQuery: searchState?.[0] || '',
    paginationParams: {
      ...(orderingState?.[0] || {}),
      ...(paginationState?.[0] || {}),
    },
    orderingParams: orderingState?.[0] || {},
    skip: !shouldRunAllQuery,
  });

  useEffect(() => {
    handleAllTabQueryResult({
      collection,
      totalCount,
      loading: departmentsLoading,
      firstLoading: departmentsFirstLoading,
    });
  }, [
    collection,
    departmentsFirstLoading,
    departmentsLoading,
    handleAllTabQueryResult,
    totalCount,
  ]);

  const {
    collection: allTabCollection,
    totalCount: allTabTotalCount,
    firstLoading: allTabFirstLoading,
  } = getAllTabSnapshot({
    collection,
    totalCount,
    firstLoading: departmentsFirstLoading,
  });

  // Departments are hierarchical, so the All tab browses them as an
  // expandable tree (root departments paginated, children lazy-loaded per
  // node) rather than a flat list — same UX as DepartmentParentSelectModal /
  // InterventionDepartmentsModal. Ancestors of every already-linked
  // department are auto-expanded so nested selections are visible on open
  // instead of requiring the user to hunt through the hierarchy.
  const departmentsTableTree = useDepartmentsTableTree({
    collection: allTabCollection,
    searchQuery: searchState?.[0],
    hasActiveFilters: false,
    autoExpandIds: isOpen ? selectedDepartments : [],
  });

  // Accumulate every department row seen (root + lazily-loaded children) for
  // the lifetime of the open modal. `allTabCollection` alone only covers root
  // rows, but the tree lets users pick nested departments too — this cache
  // is what lets the create-mode Selected-tab fallback below resolve those.
  const knownDepartmentsRef = useRef<Map<string, Department>>(new Map());
  departmentsTableTree.data.forEach((item) => {
    knownDepartmentsRef.current.set(String(item.id), item);
  });
  useEffect(() => {
    if (!isOpen) knownDepartmentsRef.current.clear();
  }, [isOpen]);

  const hasSelectedTabFilters = !!selectedTabFilters?.length;

  const {
    collection: collectionSelectedRaw,
    loading: departmentsLoadingSelected,
    firstLoading: departmentsFirstLoadingSelected,
  } = useDepartmentsQueryHook({
    searchQuery: searchStateSelected[0],
    paginationParams: {
      ...orderingStateSelected[0],
      ...paginationStateSelected[0],
    },
    orderingParams: orderingStateSelected[0],
    additionalFilters: selectedTabFilters,
    skip:
      !isOpen ||
      !isSelectedTabActive ||
      !hasSelectedTabFilters ||
      idsForSelectedTabQuery.length === 0,
  });

  // The Selected tab must always reflect the current in-session selection
  // (`idsForSelectedTabQuery`) — a flat list resolved by id, never the tree.
  // In edit mode the reverse-filter query only returns departments already
  // persisted as linked, so relying on it alone drops any department the
  // user just checked (parent or child) but hasn't saved yet. We resolve
  // each id primarily from the known-departments cache (root rows + any
  // nested rows the user has seen/expanded into), falling back to the
  // reverse-filter query's results for ids not yet cached — i.e. links that
  // existed before the modal opened and haven't been expanded into view.
  const collectionSelected = useMemo(() => {
    if (idsForSelectedTabQuery.length === 0) return [];
    const bySelectedTabQuery = new Map<string, Department>();
    if (hasSelectedTabFilters) {
      collectionSelectedRaw.forEach((item) => {
        bySelectedTabQuery.set(String(item.id), item);
      });
    }
    return idsForSelectedTabQuery
      .map(
        (id) =>
          knownDepartmentsRef.current.get(id) || bySelectedTabQuery.get(id),
      )
      .filter((item): item is Department => !!item);
    // `departmentsTableTree.data` isn't read directly, but the ref it
    // populates above is — this dependency is what makes the memo recompute
    // once newly-expanded rows land in the cache.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    collectionSelectedRaw,
    hasSelectedTabFilters,
    idsForSelectedTabQuery,
    departmentsTableTree.data,
  ]);
  const totalCountSelected = collectionSelected.length;

  const columns = getDepartmentsTableColumns({ t });

  if (
    !activeTabState ||
    !searchState ||
    !paginationState ||
    !orderingState ||
    !filterState
  ) {
    return null;
  }

  return (
    <ModalTableMultiselect<Department>
      activeTabState={activeTabState}
      caption={t('department-multiselect')}
      columns={columns}
      data={departmentsTableTree.data}
      dataSelected={collectionSelected}
      fetchIds={fetchIds}
      filterFormFields={[]}
      filterState={filterState}
      filterStateSelected={filterStateSelected}
      filterStateDefault={filterStateDefault}
      firstLoading={allTabFirstLoading}
      firstLoadingSelected={departmentsFirstLoadingSelected}
      isOpen={isOpen}
      loading={shouldRunAllQuery ? departmentsLoading : false}
      loadingSelected={departmentsLoadingSelected || isFetchingAllIds}
      onClose={onClose}
      onIsFetchingAllIdsChange={setIsFetchingAllIds}
      onResetFilter={clearFilter}
      onSelect={onSelect}
      onSelectedIdsChange={setSelectedIdsForQuery}
      orderingState={orderingState}
      orderingStateSelected={orderingStateSelected}
      paginationState={paginationState}
      paginationStateSelected={paginationStateSelected}
      searchPleceholder={t('departments-search-placeholder')}
      searchState={searchState}
      searchStateSelected={searchStateSelected}
      selected={selectedDepartments}
      totalEntries={allTabTotalCount}
      totalEntriesSelected={totalCountSelected}
      tree={departmentsTableTree.tree}
    />
  );
}

export default memo(DepartmentsModalTableMultiselect);
